import { EditorState } from "../core/editor-state";
import { getBlock, getAllBlocks } from "../blocks/registry";

const template = document.createElement("template");
template.innerHTML = `
  <style>
    :host {
      display: block;
      font-family: system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
      color: #111827;
    }

    .tnp-editor-root {
      border-radius: 8px;
      background: #f3f4f6;
      border: 1px solid #e5e7eb;
      display: flex;
      flex-direction: column;
      min-height: 400px;
      overflow: hidden;
    }

    .tnp-topbar {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 8px 12px;
      background: #111827;
      color: #f9fafb;
      font-size: 13px;
    }

    .tnp-logo {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 24px;
      height: 24px;
      border-radius: 6px;
      background: #4f46e5;
      font-size: 11px;
      font-weight: 700;
      margin-right: 6px;
    }

    .tnp-topbar-left {
      display: flex;
      align-items: center;
    }

    .tnp-topbar-actions button {
      margin-left: 4px;
      padding: 3px 8px;
      border-radius: 4px;
      border: 1px solid rgba(249,250,251,0.2);
      background: transparent;
      color: #f9fafb;
      font-size: 12px;
      cursor: pointer;
    }

    .tnp-topbar-actions button:hover {
      background: rgba(249,250,251,0.1);
    }

    .tnp-body {
      display: flex;
      gap: 12px;
      padding: 12px;
    }

    .tnp-canvas {
      flex: 1;
      background: #ffffff;
      border-radius: 8px;
      box-shadow: 0 8px 20px rgba(15, 23, 42, 0.12);
      padding: 18px 28px;
    }

    .tnp-toolbar {
      display: flex;
      flex-wrap: wrap;
      gap: 6px;
      margin-bottom: 10px;
      border-bottom: 1px solid #e5e7eb;
      padding-bottom: 6px;
    }

    .tnp-toolbar button {
      padding: 4px 8px;
      border-radius: 999px;
      border: 1px solid #e5e7eb;
      background: #f9fafb;
      font-size: 12px;
      cursor: pointer;
    }

    .tnp-toolbar button:hover {
      background: #e5e7eb;
    }

    .tnp-block-list {
      display: flex;
      flex-direction: column;
      gap: 6px;
      position: relative;
    }

    .tnp-block {
      position: relative;
      padding: 4px 6px;
      border-radius: 4px;
      border: 1px solid transparent;
    }

    .tnp-block--active {
      border-color: #4f46e5;
      background: #eef2ff;
    }

    .tnp-block-controls {
      position: absolute;
      left: -70px;
      top: 4px;
      opacity: 0;
      pointer-events: none;
      display: flex;
      gap: 2px;
    }

    .tnp-block:hover .tnp-block-controls,
    .tnp-block.tnp-block--active .tnp-block-controls {
      opacity: 1;
      pointer-events: auto;
    }

    .tnp-block-controls button {
      border: 1px solid #d1d5db;
      background: #f9fafb;
      border-radius: 4px;
      padding: 2px 5px;
      font-size: 11px;
      cursor: pointer;
    }

    .tnp-block-controls button:hover {
      background: #e5e7eb;
    }

    .tnp-block-content[contenteditable="true"] {
      outline: none;
      min-height: 18px;
      cursor: text;
    }

    .tnp-block-content--paragraph {
      font-size: 14px;
      line-height: 1.6;
    }

    .tnp-block-content--heading {
      font-size: 24px;
      font-weight: 700;
      margin: 8px 0 4px;
    }

    .tnp-sidebar {
      width: 220px;
      background: #ffffff;
      border-radius: 8px;
      box-shadow: 0 8px 20px rgba(15,23,42,0.08);
      padding: 10px;
      font-size: 12px;
    }

    .tnp-sidebar-section {
      margin-bottom: 10px;
      padding-bottom: 6px;
      border-bottom: 1px solid #e5e7eb;
    }

    .tnp-sidebar-title {
      font-size: 12px;
      font-weight: 600;
      margin-bottom: 4px;
    }

    .tnp-sidebar-row {
      display: flex;
      justify-content: space-between;
      margin-bottom: 2px;
      color: #4b5563;
    }

    .tnp-badge {
      display: inline-flex;
      align-items: center;
      padding: 2px 6px;
      border-radius: 999px;
      background: #eef2ff;
      color: #4338ca;
      font-size: 11px;
      margin-top: 4px;
    }
  </style>
  <div class="tnp-editor-root">
    <div class="tnp-topbar">
      <div class="tnp-topbar-left">
        <div class="tnp-logo">TnP</div>
        <span>TnP Editor · Alpha</span>
      </div>
      <div class="tnp-topbar-actions">
        <button data-action="log-json">Log JSON</button>
        <button data-action="log-html">Log HTML</button>
      </div>
    </div>
    <div class="tnp-body">
      <div class="tnp-canvas">
        <div class="tnp-toolbar" data-ref="toolbar"></div>
        <div class="tnp-block-list" data-ref="blockList"></div>
      </div>
      <aside class="tnp-sidebar">
        <div class="tnp-sidebar-section">
          <div class="tnp-sidebar-title">Document</div>
          <div class="tnp-sidebar-row">
            <span>Blocks</span><span data-ref="blockCount">0</span>
          </div>
          <span class="tnp-badge">From scratch</span>
        </div>
        <div class="tnp-sidebar-section">
          <div class="tnp-sidebar-title">Selection</div>
          <div class="tnp-sidebar-row">
            <span>Active ID</span><span data-ref="activeId">None</span>
          </div>
          <div class="tnp-sidebar-row">
            <span>Type</span><span data-ref="activeType">-</span>
          </div>
        </div>
      </aside>
    </div>
  </div>
`;

export class TnpEditorElement extends HTMLElement {
  private state = new EditorState();
  private blockListEl!: HTMLElement;
  private toolbarEl!: HTMLElement;
  private sidebarEls!: {
    blockCount: HTMLElement;
    activeId: HTMLElement;
    activeType: HTMLElement;
  };

  constructor() {
    super();
    const shadow = this.attachShadow({ mode: "open" });
    shadow.appendChild(template.content.cloneNode(true));

    this.toolbarEl = shadow.querySelector('[data-ref="toolbar"]') as HTMLElement;
    this.blockListEl = shadow.querySelector('[data-ref="blockList"]') as HTMLElement;
    this.sidebarEls = {
      blockCount: shadow.querySelector('[data-ref="blockCount"]') as HTMLElement,
      activeId: shadow.querySelector('[data-ref="activeId"]') as HTMLElement,
      activeType: shadow.querySelector('[data-ref="activeType"]') as HTMLElement
    };

    this.handleTopbarClick = this.handleTopbarClick.bind(this);
  }

  connectedCallback() {
    // Register topbar logs
    this.shadowRoot
      ?.querySelector(".tnp-topbar-actions")
      ?.addEventListener("click", this.handleTopbarClick);

    // Create toolbar buttons based on blocks registry
    this.renderToolbar();

    // Seed with one paragraph
    this.state.insertBlock("paragraph", "");
    this.render();
  }

  disconnectedCallback() {
    this.shadowRoot
      ?.querySelector(".tnp-topbar-actions")
      ?.removeEventListener("click", this.handleTopbarClick);
  }

  private handleTopbarClick(event: Event) {
    const btn = (event.target as HTMLElement).closest("button");
    if (!btn) return;
    const action = btn.getAttribute("data-action");
    if (action === "log-json") {
      // eslint-disable-next-line no-console
      console.log("TnP Editor JSON:", this.state.toJSON());
    }
    if (action === "log-html") {
      // eslint-disable-next-line no-console
      console.log("TnP Editor HTML:\n", this.state.toHTML());
    }
  }

  private renderToolbar() {
    const defs = getAllBlocks();
    this.toolbarEl.innerHTML = "";
    defs.forEach((def) => {
      const btn = document.createElement("button");
      btn.textContent = `${def.icon ? def.icon + " " : ""}${def.title}`;
      btn.addEventListener("click", () => {
        const blockDef = getBlock(def.type);
        this.state.insertBlock(def.type, blockDef?.defaultContent ?? "");
        this.render();
      });
      this.toolbarEl.appendChild(btn);
    });
  }

  private render() {
    this.renderBlocks();
    this.updateSidebar();
  }

  private renderBlocks() {
    this.blockListEl.innerHTML = "";

    this.state.blocks.forEach((block) => {
      const wrapper = document.createElement("div");
      wrapper.className = "tnp-block";
      if (block.id === this.state.activeBlockId) {
        wrapper.classList.add("tnp-block--active");
      }
      wrapper.dataset.blockId = block.id;

      const controls = document.createElement("div");
      controls.className = "tnp-block-controls";
      controls.innerHTML = `
        <button data-action="up">↑</button>
        <button data-action="down">↓</button>
        <button data-action="delete">✕</button>
      `;
      controls.addEventListener("click", (e) => {
        e.stopPropagation();
        const btn = (e.target as HTMLElement).closest("button");
        if (!btn) return;
        const action = btn.getAttribute("data-action");
        if (action === "up") this.state.moveBlock(block.id, "up");
        if (action === "down") this.state.moveBlock(block.id, "down");
        if (action === "delete") this.state.removeBlock(block.id);
        this.render();
      });

      const tag = block.type === "heading" ? "h2" : "p";
      const contentEl = document.createElement(tag);
      contentEl.className =
        "tnp-block-content " +
        (block.type === "heading"
          ? "tnp-block-content--heading"
          : "tnp-block-content--paragraph");
      contentEl.contentEditable = "true";
      contentEl.innerText = block.attributes.content;

      contentEl.addEventListener("input", (e) => {
        const value = (e.target as HTMLElement).innerText;
        this.state.updateBlock(block.id, { content: value });
      });

      wrapper.addEventListener("click", () => {
        this.state.activeBlockId = block.id;
        this.render();
      });

      wrapper.appendChild(controls);
      wrapper.appendChild(contentEl);
      this.blockListEl.appendChild(wrapper);
    });
  }

  private updateSidebar() {
    this.sidebarEls.blockCount.textContent = String(this.state.blocks.length);
    const active =
      this.state.blocks.find((b) => b.id === this.state.activeBlockId) || null;
    this.sidebarEls.activeId.textContent = active ? active.id : "None";
    this.sidebarEls.activeType.textContent = active ? active.type : "-";
  }

  // Public API (for later)
  public getValueAsJSON() {
    return this.state.toJSON();
  }

  public getValueAsHTML() {
    return this.state.toHTML();
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "tnp-editor": TnpEditorElement;
  }
}

if (!customElements.get("tnp-editor")) {
  customElements.define("tnp-editor", TnpEditorElement);
}
